import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { CheckCircle, Loader2, AlertCircle } from 'lucide-react';

const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading'); // 'loading', 'success', 'error'
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    
    if (!sessionId) {
      setStatus('error');
      setError('No session ID found');
      return;
    }

    handleCheckoutSuccess(sessionId);
  }, [searchParams]);

  const handleCheckoutSuccess = async (sessionId) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/stripe/checkout-success`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ sessionId })
      });

      const data = await response.json();

      if (data.success) {
        setStatus('success');
        
        // Fire Meta Pixel conversion event for free trial start
        if (typeof window.fbq !== 'undefined') {
          window.fbq('track', 'StartTrial', {
            content_name: 'Impact Accelerator Circle Membership',
            content_category: 'Subscription',
            value: 0, // Free trial, no payment yet
            currency: 'USD'
          });
        }
        
        // Don't redirect - user needs to check email and set password first
      } else {
        setStatus('error');
        setError(data.message || 'Failed to process checkout');
      }
    } catch (error) {
      console.error('Checkout success error:', error);
      setStatus('error');
      setError('Failed to process checkout. Please contact support.');
    }
  };

  const renderContent = () => {
    switch (status) {
      case 'loading':
        return (
          <div className="text-center">
            <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Processing Your Payment</h2>
            <p className="text-gray-600">Please wait while we set up your account...</p>
          </div>
        );

      case 'success':
        return (
          <div className="text-center">
            <div className="mb-6">
              <CheckCircle className="w-20 h-20 text-green-600 mx-auto mb-4 animate-pulse" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-3">
              Congratulations! 🎉
            </h2>
            <p className="text-lg font-semibold text-gray-700 mb-2">
              You just made the best decision of this quarter.
            </p>
            <div className="bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-200 rounded-xl p-6 mb-6">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Next Steps:</h3>
              <p className="text-base text-gray-700 font-medium">
                Check your email to activate your portal access
              </p>
              <p className="text-sm text-gray-600 mt-3">
                We've sent you an activation email with a secure link to set your password and complete your account setup.
              </p>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-yellow-800">
                <strong>Don't see the email?</strong> It may take a few minutes to arrive. Check your spam folder if needed.
              </p>
            </div>
            <div className="space-y-2">
              <Button 
                onClick={() => navigate('/login')}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg"
              >
                Go to Sign In
              </Button>
              <Button 
                onClick={() => navigate('/')}
                variant="outline"
                className="w-full"
              >
                Go to Home
              </Button>
            </div>
          </div>
        );

      case 'error':
        return (
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Something Went Wrong</h2>
            <p className="text-gray-600 mb-6">
              {error || 'We encountered an issue processing your payment.'}
            </p>
            <div className="space-y-3">
              <Button 
                onClick={() => navigate('/register')}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg"
              >
                Try Again
              </Button>
              <Button 
                onClick={() => navigate('/')}
                variant="outline"
                className="w-full"
              >
                Go Home
              </Button>
            </div>
            <p className="text-xs text-gray-500 mt-4">
              If this problem persists, please contact support with your session details.
            </p>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card className="shadow-xl">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold text-gray-900">
              Checkout Status
            </CardTitle>
            <CardDescription>
              Processing your Impact Accelerator Circle membership
            </CardDescription>
          </CardHeader>
          <CardContent>
            {renderContent()}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CheckoutSuccessPage;
